// src/store/selectors.js
// Canonical selectors (initiative-first, no legacy fallbacks).

const EMPTY_OBJ = Object.freeze({});
const EMPTY_ARR = Object.freeze([]);

function safeArr(v) {
  return Array.isArray(v) ? v : EMPTY_ARR;
}

function safeText(v) {
  return v == null ? "" : String(v);
}

function normalizeVersion(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function computeWorkflowNavState({ initiative, project, journeys }) {
  const init = initiative || null;
  const proj = project || null;
  const journeyList = Array.isArray(journeys) ? journeys : [];

  const researchItems = safeArr(init?.researchItems);
  const hasResearch = researchItems.length > 0;
  const hasMeaningful = researchItems.some((item) => {
    const text = safeText(item?.normalizedText || item?.rawText || item?.text).trim();
    return text.length > 0;
  });
  const researchStatus = !hasResearch ? "empty" : hasMeaningful ? "complete" : "in_progress";

  const researchVersion = normalizeVersion(init?.researchVersion, hasResearch ? 1 : 0);

  const insightsItems = safeArr(init?.researchInsights?.items);
  const insightsApprovalStatus = safeText(init?.insightsApprovalStatus).trim().toLowerCase();
  const insightsApprovedAt = Number.isFinite(Number(init?.insightsApprovedAt))
    ? Number(init.insightsApprovedAt)
    : null;
  const insightsEditedAt = Number.isFinite(Number(init?.insightsEditedAt))
    ? Number(init.insightsEditedAt)
    : null;
  const insightsDerived = normalizeVersion(init?.insightsMeta?.derivedFromResearchVersion, researchVersion);
  const hasApprovedInsights = insightsApprovalStatus === "approved" || Boolean(insightsApprovedAt);
  const editedAfterApproval =
    hasApprovedInsights && insightsApprovedAt && insightsEditedAt && insightsEditedAt > insightsApprovedAt;
  const researchUpdatedAfterApproval =
    hasApprovedInsights && researchVersion > 1 && insightsDerived < researchVersion;

  let insightsStatus = "empty";
  if (insightsItems.length === 0) {
    insightsStatus = "empty";
  } else if (editedAfterApproval || researchUpdatedAfterApproval) {
    insightsStatus = "stale";
  } else if (insightsApprovalStatus && insightsApprovalStatus !== "approved") {
    insightsStatus = "in_progress";
  } else {
    insightsStatus = "complete";
  }

  const tagVocabulary = safeArr(init?.tagVocabulary?.items);
  const tagSpans = safeArr(init?.researchTagSpans?.items);
  const tagSet = new Set();
  tagVocabulary.forEach((it) => {
    const tag = safeText(it?.tag || it?.name || it?.label).trim().toLowerCase();
    if (tag) tagSet.add(tag);
  });
  tagSpans.forEach((it) => {
    const tag = safeText(it?.tag).trim().toLowerCase();
    if (tag) tagSet.add(tag);
  });
  const tagsCount = tagSet.size;
  const tagsDerived = normalizeVersion(init?.tagsMeta?.derivedFromResearchVersion, researchVersion);

  let tagsStatus = "empty";
  if (tagsCount === 0) {
    tagsStatus = "empty";
  } else if (tagsDerived < researchVersion) {
    tagsStatus = "stale";
  } else {
    tagsStatus = "complete";
  }

  const notesCount = safeArr(init?.notes).length;
  const notesStatus = notesCount > 0 ? "complete" : "empty";

  const themeCount = safeArr(init?.tagThemes?.themes).length;
  const themesStatus = themeCount > 0 ? "complete" : insightsItems.length > 0 ? "in_progress" : "empty";

  const personasCount = safeArr(proj?.personas).length;
  const personasStatus = personasCount > 0 ? "complete" : "empty";

  const problemStatementsCount = safeArr(init?.problemStatements).length;
  const problemStatementsStatus = problemStatementsCount > 0 ? "complete" : "empty";

  const jtbdsCount = safeArr(init?.jtbds).length;
  const jtbdsStatus = jtbdsCount > 0 ? "complete" : problemStatementsCount > 0 ? "in_progress" : "empty";

  const decisionsCount = safeArr(init?.decisions).length;
  const decisionsStatus = decisionsCount > 0 ? "complete" : "empty";

  const journeyStatus =
    journeyList.length > 0 ? "complete" : personasCount > 0 ? "in_progress" : "empty";

  return {
    researchInput: { status: researchStatus, count: researchItems.length },
    researchNotes: { status: notesStatus, count: notesCount },
    insightsTags: { status: tagsStatus, count: tagSet.size },
    insightsReview: { status: insightsStatus, count: insightsItems.length },
    synthesisThemes: { status: themesStatus, count: themeCount },
    synthesisPersonas: { status: personasStatus, count: personasCount },
    synthesisJourney: { status: journeyStatus, count: journeyList.length },
    synthesisProblemStatement: { status: problemStatementsStatus, count: problemStatementsCount },
    synthesisJTBD: { status: jtbdsStatus, count: jtbdsCount },
    decisionsOpportunities: { status: decisionsStatus, count: decisionsCount },
    decisionsHmw: { status: "empty" },
    decisionsRoadmap: { status: "empty" },
    validateExperiments: { status: "coming_soon" },
    validateMetrics: { status: "coming_soon" },
  };
}

export function computeNavState(s) {
  return computeWorkflowNavState({
    initiative: selectActiveInitiative(s),
    project: selectActiveProject(s),
    journeys: selectJourneys(s),
  });
}

function resolveProject(s, projectId) {
  const pid = projectId || selectActiveProjectId(s);
  const byId = selectProjectsById(s);
  return (pid && byId[pid]) || null;
}

function resolveInitiative(s, projectId, initiativeId) {
  const project = resolveProject(s, projectId);
  const pid = project?.id || projectId;
  const map = s?.activeInitiativeByProjectId || {};
  const iid =
    initiativeId ||
    (pid && map[pid]) ||
    project?.initiatives?.activeId ||
    project?.initiatives?.allIds?.[0] ||
    null;
  const byId = project?.initiatives?.byId || {};
  return (iid && byId[iid]) || null;
}

export function selectActiveWorkspaceId(s) {
  return s?.activeWorkspaceId ?? null;
}

export function selectActiveProjectId(s) {
  const wsId = selectActiveWorkspaceId(s);
  const map = s?.activeProjectByWorkspaceId || {};
  return (wsId && map[wsId]) || null;
}

export function selectProjectsById(s) {
  return s?.projects?.byId || EMPTY_OBJ;
}

export function selectActiveProject(s) {
  const pid = selectActiveProjectId(s);
  const byId = selectProjectsById(s);
  return (pid && byId[pid]) || null;
}

export function selectActiveInitiativeId(s) {
  const pid = selectActiveProjectId(s);
  const map = s?.activeInitiativeByProjectId || {};
  const project = selectActiveProject(s);
  return (pid && map[pid]) || project?.initiatives?.activeId || project?.initiatives?.allIds?.[0] || null;
}

export function selectActiveInitiative(s) {
  const project = selectActiveProject(s);
  const iid = selectActiveInitiativeId(s);
  const byId = project?.initiatives?.byId || {};
  return (iid && byId[iid]) || null;
}

export function selectMeta(s) {
  const project = selectActiveProject(s);
  return project?.meta || EMPTY_OBJ;
}

export function selectPersonas(s) {
  const project = selectActiveProject(s);
  return safeArr(project?.personas);
}

export function selectResearchItems(s) {
  const init = selectActiveInitiative(s);
  return safeArr(init?.researchItems);
}

export function selectResearchInsights(s) {
  const init = selectActiveInitiative(s);
  return init?.researchInsights || null;
}

export function selectResearchTagSpans(s) {
  const init = selectActiveInitiative(s);
  return init?.researchTagSpans || null;
}

export function selectNotesForResearchItem(initiativeId, researchItemId) {
  return (s) => {
    const init = resolveInitiative(s, null, initiativeId);
    const notes = safeArr(init?.notes);
    const rid = typeof researchItemId === "string" ? researchItemId.trim() : "";
    if (!rid) return [];
    return notes.filter((note) => note?.researchItemId === rid);
  };
}

export function selectOpenNotesCount(initiativeId) {
  return (s) => {
    const init = resolveInitiative(s, null, initiativeId);
    const notes = safeArr(init?.notes);
    return notes.filter((note) => note?.status !== "resolved").length;
  };
}

export function selectNotesIntersectingSpan(initiativeId, researchItemId, startOffset, endOffset) {
  return (s) => {
    const init = resolveInitiative(s, null, initiativeId);
    const notes = safeArr(init?.notes);
    const rid = typeof researchItemId === "string" ? researchItemId.trim() : "";
    const start = Number(startOffset);
    const end = Number(endOffset);
    if (!rid || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
    return notes.filter((note) => {
      if (note?.researchItemId !== rid) return false;
      const nStart = Number(note?.startOffset);
      const nEnd = Number(note?.endOffset);
      if (!Number.isFinite(nStart) || !Number.isFinite(nEnd)) return false;
      return Math.max(nStart, start) < Math.min(nEnd, end);
    });
  };
}

export function selectTagVocabulary(s) {
  const init = selectActiveInitiative(s);
  return init?.tagVocabulary || null;
}

export function selectTagThemes(s) {
  const init = selectActiveInitiative(s);
  return init?.tagThemes || null;
}

export function selectJourneys(s) {
  return Array.isArray(s?.journeys) ? s.journeys : EMPTY_ARR;
}

export function selectResearchNotes(s) {
  return Array.isArray(s?.researchNotes) ? s.researchNotes : EMPTY_ARR;
}

function resolveInitiativeForProject(project, activeInitByProjectId) {
  if (!project) return null;
  const byId = project?.initiatives?.byId || EMPTY_OBJ;
  const pid = project?.id || null;
  const activeId =
    (pid && activeInitByProjectId?.[pid]) ||
    project?.initiatives?.activeId ||
    project?.initiatives?.allIds?.[0] ||
    null;
  return (activeId && byId[activeId]) || null;
}

export function computeAppPhaseForProject(s, projectId) {
  const project = projectId ? s?.projects?.byId?.[projectId] || null : selectActiveProject(s);
  if (!project) return "wizard";
  const init = resolveInitiativeForProject(project, s?.activeInitiativeByProjectId || {});

  const researchItems = safeArr(init?.researchItems);
  const hasResearch = researchItems.length > 0;

  const wizardCompleted = project?.meta?.wizardCompleted;
  const insightsApproved = String(init?.insightsApprovalStatus || "").toLowerCase() === "approved";
  const insightsItems = safeArr(init?.researchInsights?.items);
  const tagSpans = safeArr(init?.researchTagSpans?.items);
  const tagVocabulary = safeArr(init?.tagVocabulary?.items);
  const hasOutputs = insightsItems.length > 0 || tagSpans.length > 0 || tagVocabulary.length > 0;

  if (wizardCompleted === false) return "wizard";
  if (wizardCompleted === true && !insightsApproved && hasOutputs) return "review";
  if (insightsApproved) return "workspace";
  if (wizardCompleted === true && hasResearch && !hasOutputs) return "review";
  return "workspace";
}

export function selectInsightGroups(s) {
  const init = selectActiveInitiative(s);
  return init?.insightGroups || null;
}

export function selectInterviewQuestions(s) {
  const init = selectActiveInitiative(s);
  return init?.interviewQuestions || EMPTY_OBJ;
}

export function selectResearchScripts(s) {
  const init = selectActiveInitiative(s);
  return safeArr(init?.interviewScripts);
}

export function selectDecisions(projectId, initiativeId) {
  return (s) => {
    const init = resolveInitiative(s, projectId, initiativeId);
    return safeArr(init?.decisions);
  };
}

export function selectDecisionsForOverview(projectId, initiativeId) {
  return (s) => {
    const init = resolveInitiative(s, projectId, initiativeId);
    const list = safeArr(init?.decisions);
    return [...list].sort((a, b) => {
      const aTime = Date.parse(a?.updatedAt || a?.createdAt || 0) || 0;
      const bTime = Date.parse(b?.updatedAt || b?.createdAt || 0) || 0;
      return bTime - aTime;
    });
  };
}

export function selectInsightsLinkedToDecision(projectId, initiativeId, decisionId) {
  return (s) => {
    const init = resolveInitiative(s, projectId, initiativeId);
    const decisions = safeArr(init?.decisions);
    const decision = decisions.find((d) => d.id === decisionId);
    const linkedIds = new Set(Array.isArray(decision?.linkedInsightIds) ? decision.linkedInsightIds : []);
    const insights = safeArr(init?.researchInsights?.items);
    return insights.filter((insight) => linkedIds.has(insight.id));
  };
}

export function selectRecentActivity(projectId, limit = 50) {
  return (s) => {
    const project = resolveProject(s, projectId);
    const activity = safeArr(project?.activity);
    const sorted = [...activity].sort((a, b) => {
      const aTime = Date.parse(a?.timestamp || 0) || 0;
      const bTime = Date.parse(b?.timestamp || 0) || 0;
      return bTime - aTime;
    });
    return sorted.slice(0, Number.isFinite(Number(limit)) ? Number(limit) : 50);
  };
}

export function selectActivitySince(projectId, timestamp) {
  return (s) => {
    const project = resolveProject(s, projectId);
    const activity = safeArr(project?.activity);
    const since = Date.parse(timestamp || 0) || 0;
    return activity.filter((entry) => (Date.parse(entry?.timestamp || 0) || 0) > since);
  };
}

export function selectFeedbackList({ status, type } = {}) {
  return (s) => {
    const list = safeArr(s?.feedback);
    return list.filter((item) => {
      if (status && item.status !== status) return false;
      if (type && item.type !== type) return false;
      return true;
    });
  };
}

export function selectNewFeedbackCount() {
  return (s) => {
    const list = safeArr(s?.feedback);
    return list.filter((item) => item.status === "new").length;
  };
}
